import { yodaCatalogueConstants } from "../../../logic/constants/yodaCatalogue.constants";
import { yodaOrdersConstants } from "../../../logic/constants/yodaOrders.constants";
import { yodaFavoritesConstants } from "../../../logic/constants/yodaFavorites.constants";
import { yodaNotificationsConstants } from "../../../logic/constants/yodaNotifications.constants";
import i18n from "../../../i18n";
import { pushToDataLayer } from "./dataLayer";
import { GtmEvent } from "./events";
import {
  buildCartEventPayload,
  buildOrderErrorEvent,
  buildPurchaseEvent,
} from "./mappers/orderMapper";
import {
  mapProductDetailToViewItems,
  mapSingleVariantToItem,
  serialiseProductItemForGtm,
} from "./mappers/productMapper";
import { buildUserContext } from "./mappers/userMapper";
import {
  selectCatalogueState,
  selectCustomer,
  selectOrderDetails,
  selectOrderMetadata,
  selectOrderRows,
  selectStakeholder,
  selectPersistedCustomerId,
  selectPersistedAgentId,
  selectOrderConfiguration,
} from "./selectors";
import { safeNumber, stripUndefined } from "./utils";

const CART_MUTATION_TYPES = new Set([
  yodaOrdersConstants.ADD_ROW_SUCCESS,
  yodaOrdersConstants.UPDATE_ROW_SUCCESS,
  yodaOrdersConstants.CONCURRENT_UPDATE_ROW_SINGLE_SUCCESS,
  yodaOrdersConstants.CONCURRENT_UPDATE_ROW_AUTOCOMPLETE_SUCCESS,
  yodaOrdersConstants.CONCURRENT_UPDATE_ROW_SUCCESS_AUTOCANCELLED,
  yodaOrdersConstants.REMOVE_PRODUCT_SUCCESS,
]);

const diffCartRows = (previousRows = {}, nextRows = {}) => {
  const additions = [];
  const removals = [];

  Object.keys(nextRows).forEach((rowId) => {
    const nextRow = nextRows[rowId];
    const prevRow = previousRows[rowId];

    const prevQuantity =
      safeNumber(prevRow?.quantity || prevRow?.currentQuantity) || 0;
    const nextQuantity =
      safeNumber(nextRow?.quantity || nextRow?.currentQuantity) || 0;
    const delta = nextQuantity - prevQuantity;

    if (delta > 0) {
      additions.push({ row: nextRow, quantity: delta });
    }

    if (delta < 0) {
      removals.push({ row: nextRow, quantity: Math.abs(delta) });
    }
  });

  Object.keys(previousRows).forEach((rowId) => {
    if (!nextRows[rowId]) {
      const prevRow = previousRows[rowId];
      const prevQuantity = safeNumber(
        prevRow?.quantity || prevRow?.currentQuantity,
      );
      if (prevQuantity) {
        removals.push({ row: prevRow, quantity: prevQuantity });
      }
    }
  });

  return { additions, removals };
};

const emitViewItem = (action, state) => {
  if (!action?.meta?.trackView) {
    return;
  }

  const product = action?.payload?.item;
  if (!product) {
    return;
  }

  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  const { order } = selectOrderMetadata(state);
  const doorCode =
    action?.meta?.viewContext?.doorCode ||
    (Array.isArray(order?.customData?.doors) &&
    order.customData.doors.length === 1
      ? String(order.customData.doors[0])
      : undefined);

  const viewContext = {
    ...(action?.meta?.viewContext || {}),
    doorCode,
  };

  const rawItems = mapProductDetailToViewItems(product, {
    language: i18n?.resolvedLanguage || i18n?.language,
    context: viewContext,
  });

  if (!rawItems.length) {
    return;
  }

  const formattedItems = rawItems
    .map((item) =>
      serialiseProductItemForGtm(item, {
        doorCode: viewContext?.doorCode,
        includeName: true,
        includeQuantita: false,
      }),
    )
    .filter((item) => Object.keys(item).length > 0);

  if (!formattedItems.length) {
    return;
  }

  const isSwapAction =
    action.type === yodaCatalogueConstants.GETONESWAP_SUCCESS;
  const orderTypeCode =
    action?.meta?.viewContext?.orderType === "SWAP" || isSwapAction ? "S" : "R";

  pushToDataLayer(
    stripUndefined({
      event: GtmEvent.VIEW_ITEM,
      account: userContext?.account,
      role: userContext?.role,
      Cd_TipoOrdine: orderTypeCode,
      ecommerce: {
        items: formattedItems,
      },
    }),
  );
};

const emitCartDiffEvents = ({ event, diffs, state }) => {
  if (!diffs || !diffs.length) {
    return;
  }

  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  const { order } = selectOrderMetadata(state);
  const catalogueState = selectCatalogueState(state);

  diffs
    .map(({ row, quantity }) =>
      buildCartEventPayload({
        event,
        row,
        quantity,
        catalogueState,
        userContext,
        order,
        customer,
        language: i18n?.resolvedLanguage || i18n?.language,
      }),
    )
    .filter(Boolean)
    .forEach(pushToDataLayer);
};

const normaliseRows = (rows = []) =>
  rows
    .filter((row) => safeNumber(row?.quantity || row?.currentQuantity) > 0)
    .map((row) => {
      const baseDetail = Array.isArray(row?.orderRowsDetail)
        ? row.orderRowsDetail[0]
        : undefined;

      const detailFallback = {
        // row.catalogId = cart-level ID (stesso per tutte le righe) → non affidabile.
        // Nelle righe normalizzate per PURCHASE non abbiamo orderRowsDetail,
        // ma il baseDetail (quando presente) ha il catalogId corretto per riga.
        catalogId: row.catalogId,
        sizeRowId: row.sizeRowId,
        quantity: row.quantity || row.currentQuantity,
        price: row.price,
        productId: row.productId,
        modelCode: row.modelCode,
        materialCode: row.materialCode,
        lineCode: row.lineCode,
        collectionCode: row.collectionCode,
        seasonCode: row.seasonCode,
        colorCode: row.colorCode,
        customData: row.customData,
      };

      const detail = baseDetail || detailFallback;

      return {
        ...row,
        id:
          row.id ||
          `${row.orderId || row.id || "order"}_${detail.sizeRowId || detail.catalogId || detail.productId || detail.id || "row"}`,
        orderRowsDetail: [detail],
      };
    });

const buildRowsForPurchase = (state, action) => {
  const rowsFromState = normaliseRows(
    Object.values(selectOrderRows(state) || {}),
  );
  if (rowsFromState.length) {
    return rowsFromState;
  }

  const detailRows = normaliseRows(selectOrderDetails(state));
  if (detailRows.length) {
    return detailRows;
  }

  const orderPayload = action?.payload?.order;
  const details = orderPayload?.orderRowsDetail;
  if (!Array.isArray(details) || !details.length) {
    return [];
  }

  return details
    .filter((detail) => safeNumber(detail?.quantity) > 0)
    .map((detail) => ({
      ...orderPayload,
      id: `${orderPayload.id}_${detail.sizeRowId || detail.catalogId || detail.id || detail.productId || "row"}`,
      catalogId: detail.catalogId ?? orderPayload.catalogId,
      sizeRowId: detail.sizeRowId,
      quantity: detail.quantity,
      currentQuantity: detail.quantity,
      price: detail.price,
      productId: detail.productId ?? orderPayload.productId,
      modelCode: detail.modelCode ?? orderPayload.modelCode,
      materialCode: detail.materialCode ?? orderPayload.materialCode,
      lineCode: detail.lineCode ?? orderPayload.lineCode,
      collectionCode: detail.collectionCode ?? orderPayload.collectionCode,
      seasonCode: detail.seasonCode ?? orderPayload.seasonCode,
      modelDescription:
        detail.modelDescription ?? orderPayload.modelDescription,
      collectionDescription:
        detail.collectionDescription ?? orderPayload.collectionDescription,
      customData: {
        ...(orderPayload.customData || {}),
        ...(detail.customData || {}),
      },
      orderRowsDetail: [detail],
    }));
};

const emitPurchaseEvent = (state, action, orderSnapshot) => {
  const { order: orderFromState } = selectOrderMetadata(state);
  // orderSnapshot catturato PRIMA di next(action) per evitare che il reducer lo azzeri
  const order = orderSnapshot || orderFromState || action?.payload?.order;
  const rows = buildRowsForPurchase(state, action);

  if (!order || !rows.length) {
    return;
  }

  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  const payload = buildPurchaseEvent({
    order,
    rows,
    catalogueState: selectCatalogueState(state),
    customer,
    userContext,
    language: i18n?.resolvedLanguage || i18n?.language,
    configuration: selectOrderConfiguration(state),
  });

  if (payload) {
    pushToDataLayer(payload);
  }
};

const emitOrderError = (state, action) => {
  const { order } = selectOrderMetadata(state);
  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  const payload = buildOrderErrorEvent({
    order,
    userContext,
    error: action?.payload?.error || action?.payload,
  });

  if (payload) {
    pushToDataLayer(payload);
  }
};

const emitWishlistEvent = (event, action, state) => {
  const { catalogId, value } = action?.payload || {};

  if (!catalogId) {
    return;
  }

  const catalogueState = selectCatalogueState(state);
  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  // Cerca il prodotto nel catalogo usando catalogId
  const allProducts = {
    ...catalogueState?.catalogueItemDetails,
    ...catalogueState?.catalogueItemDetailsSwap,
  };

  let product = null;
  for (const productId in allProducts) {
    const p = allProducts[productId];
    if (p?.catalog?.some((cat) => cat.catalogId === catalogId)) {
      product = p;
      break;
    }
  }

  if (!product) {
    return;
  }

  // Mappa SOLO la variante specifica su cui l'utente ha agito
  const singleItem = mapSingleVariantToItem({
    product,
    catalogId,
    language: i18n?.resolvedLanguage || i18n?.language,
    context: {},
  });

  if (!singleItem) {
    return;
  }

  const formattedItem = serialiseProductItemForGtm(singleItem, {
    doorCode: singleItem?.Cd_Door,
    includeName: true,
    includeQuantita: false,
  });

  if (!formattedItem || Object.keys(formattedItem).length === 0) {
    return;
  }

  const gtmEvent = value
    ? GtmEvent.ADD_TO_WISHLIST
    : GtmEvent.REMOVE_FROM_WISHLIST;

  pushToDataLayer(
    stripUndefined({
      event: gtmEvent,
      account: userContext?.account,
      role: userContext?.role,
      ecommerce: {
        items: [formattedItem],
      },
    }),
  );
};

const emitNotifyMeEvent = (action, state) => {
  const { catalogId, value, sizeRowid } = action?.payload || {};

  // Traccia solo quando l'utente si iscrive (value = true)
  if (!catalogId || !value) {
    return;
  }

  const catalogueState = selectCatalogueState(state);
  const stakeholder = selectStakeholder(state);
  const customer = selectCustomer(state);
  const persistedCustomerId = selectPersistedCustomerId();
  const persistedAgentId = selectPersistedAgentId();

  const userContext = buildUserContext({
    stakeholder,
    customer,
    persistedCustomerId,
    persistedAgentId,
  });

  // Cerca il prodotto nel catalogo usando catalogId
  const allProducts = {
    ...catalogueState?.catalogueItemDetails,
    ...catalogueState?.catalogueItemDetailsSwap,
  };

  let product = null;
  for (const productId in allProducts) {
    const p = allProducts[productId];
    if (p?.catalog?.some((cat) => cat.catalogId === catalogId)) {
      product = p;
      break;
    }
  }

  if (!product) {
    return;
  }

  // Mappa SOLO la variante specifica su cui l'utente ha agito.
  // sizeRowid (dalla action) identifica univocamente la taglia; catalogId è a livello di
  // colore quindi senza sizeRowId produrrebbe la prima taglia trovata per quel colore.
  const singleItem = mapSingleVariantToItem({
    product,
    catalogId,
    sizeRowId: sizeRowid,
    language: i18n?.resolvedLanguage || i18n?.language,
    context: {},
  });

  if (!singleItem) {
    return;
  }

  const formattedItem = serialiseProductItemForGtm(singleItem, {
    doorCode: singleItem?.Cd_Door,
    includeName: true,
    includeQuantita: false,
  });

  if (!formattedItem || Object.keys(formattedItem).length === 0) {
    return;
  }

  pushToDataLayer(
    stripUndefined({
      event: GtmEvent.SUBSCRIBE_BACK_IN_STOCK,
      account: userContext?.account,
      role: userContext?.role,
      ecommerce: {
        items: [formattedItem],
      },
    }),
  );
};

export const createGtmMiddleware =
  () =>
  ({ getState }) =>
  (next) =>
  (action) => {
    const shouldDiffCart = CART_MUTATION_TYPES.has(action.type);
    const previousRows = shouldDiffCart
      ? selectOrderRows(getState())
      : undefined;

    // Cattura l'ordine PRIMA che next(action) lo sovrascriva nel reducer
    const orderSnapshot =
      action.type === yodaOrdersConstants.WAITING_FOR_CONFIRMATION_SUCCESS
        ? selectOrderMetadata(getState()).order
        : undefined;

    const result = next(action);

    const state = getState();

    switch (action.type) {
      case yodaCatalogueConstants.GETONE_SUCCESS:
      case yodaCatalogueConstants.GETONESWAP_SUCCESS:
        emitViewItem(action, state);
        break;

      case yodaOrdersConstants.WAITING_FOR_CONFIRMATION_SUCCESS:
        emitPurchaseEvent(state, action, orderSnapshot);
        break;

      case yodaOrdersConstants.WAITING_FOR_CONFIRMATION_FAILURE:
      case yodaOrdersConstants.TRANSMIT_ORDER_FAILURE:
        emitOrderError(state, action);
        break;

      case yodaFavoritesConstants.UPSERT_FAVORITE_SUCCESS:
        emitWishlistEvent(null, action, state);
        break;

      case yodaNotificationsConstants.UPSERT_NOTIFICATION_SUCCESS:
        emitNotifyMeEvent(action, state);
        break;

      default:
        break;
    }

    if (shouldDiffCart) {
      const nextRows = selectOrderRows(state);
      const { additions, removals } = diffCartRows(previousRows, nextRows);

      if (additions.length) {
        emitCartDiffEvents({
          event: GtmEvent.ADD_TO_CART,
          diffs: additions,
          state,
        });
      }

      if (removals.length) {
        emitCartDiffEvents({
          event: GtmEvent.REMOVE_FROM_CART,
          diffs: removals,
          state,
        });
      }
    }

    return result;
  };

export const gtmMiddleware = createGtmMiddleware();
